// frontend/src/components/ExpressionEditor/ExpressionEditor.tsx
import React, { useState, useRef, useCallback, useLayoutEffect } from 'react';
import { DndContext, DragOverlay, rectIntersection } from '@dnd-kit/core';
import { useAppStore } from '../../state/store';
import AvailableSeries from './AvailableSeries';
import AvailableConstants from './AvailableConstants';
import AvailableFunctions from './AvailableFunctions';
import OperatorPalette from './OperatorPalette';
import ExpressionCanvas from './ExpressionCanvas';
import SeriesConfigDialog from './SeriesConfigDialog';
import { buildAvailableSeries } from './expressionEditorSelectors';
import {
  createInsertLiteralHandler,
  createUpdateEpsilonHandler,
  createRemoveOperandHandler,
  createClearExpressionHandler,
} from './expressionCanvasHandlers';
import { handleExpressionCanvasDragEnd } from './handleExpressionCanvasDragEnd';
import Toast from './Toast';
import { useSeriesConfigDialog } from './useSeriesConfigDialog';
import { useExpressionEditorDrag } from './useExpressionEditorDrag';

interface ExpressionEditorProps {
  /** Which graph to edit: entry (default) or exit. */
  graphKey?: 'entry' | 'exit';
}

const ExpressionEditor: React.FC<ExpressionEditorProps> = ({ graphKey = 'entry' }) => {
  const [toast, setToast] = useState<string | null>(null);
  const [layoutBudgetPx, setLayoutBudgetPx] = useState(400);
  const editorMainColRef = useRef<HTMLDivElement>(null);

  const instruments = useAppStore((state) => state.instruments);
  const indicators = useAppStore((state) => state.indicators);
  const constants = useAppStore((state) => state.constants);
  const addConstant = useAppStore((s) => s.addConstant);
  const updateConstant = useAppStore((s) => s.updateConstant);
  const removeConstant = useAppStore((s) => s.removeConstant);

  const dialog = useSeriesConfigDialog(graphKey);
  const { expressionGraph, setExpressionGraph } = dialog;

  const rootRef = useRef<HTMLDivElement>(null!);
  const availableSeries = buildAvailableSeries(instruments, indicators);

  const onInsertLiteral = React.useMemo(
    () => createInsertLiteralHandler(setExpressionGraph),
    [setExpressionGraph]
  );
  const onUpdateEpsilon = React.useMemo(
    () => createUpdateEpsilonHandler(setExpressionGraph),
    [setExpressionGraph]
  );
  const onRemoveOperand = React.useMemo(
    () => createRemoveOperandHandler(setExpressionGraph),
    [setExpressionGraph]
  );
  const onClearExpression = React.useMemo(
    () => createClearExpressionHandler(setExpressionGraph),
    [setExpressionGraph]
  );

  const handleDragEndCallback = useCallback(
    (event: any) => {
      handleExpressionCanvasDragEnd(event, {
        rootRef,
        availableSeries,
        instruments,
        indicators,
        expressionGraph,
        setExpressionGraph: setExpressionGraph,
        setDialogSeriesLabel: dialog.setDialogSeriesLabel,
        setDialogIsOHLC: dialog.setDialogIsOHLC,
        setDialogTargetSlotId: dialog.setDialogTargetSlotId,
        setDialogSeriesId: dialog.setDialogSeriesId,
        setDialogOpen: dialog.setDialogOpen,
        setDialogFunctionId: dialog.setDialogFunctionId,
        setDialogExistingTypes: dialog.setDialogExistingTypes,
        setDialogExpectedType: dialog.setDialogExpectedType,
        setCompactDialog: dialog.setCompactDialog,
        setDialogAvailableFields: dialog.setDialogAvailableFields,
        setDialogDefaultField: dialog.setDialogDefaultField,
        showToast: setToast,
      });
    },
    [
      rootRef,
      availableSeries,
      instruments,
      indicators,
      expressionGraph,
      setExpressionGraph,
      dialog.setDialogSeriesLabel,
      dialog.setDialogIsOHLC,
      dialog.setDialogTargetSlotId,
      dialog.setDialogSeriesId,
      dialog.setDialogOpen,
      dialog.setDialogFunctionId,
      dialog.setDialogExistingTypes,
      dialog.setDialogExpectedType,
      dialog.setCompactDialog,
      dialog.setDialogAvailableFields,
      dialog.setDialogDefaultField,
    ]
  );

  const drag = useExpressionEditorDrag({
    expressionGraph,
    rootRef,
    onDragEndCallback: handleDragEndCallback,
  });

  useLayoutEffect(() => {
    const el = editorMainColRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const update = () => {
      const w = el.clientWidth;
      if (w <= 0) return;
      setLayoutBudgetPx(Math.max(220, Math.floor(w * 0.88)));
    };
    update();
    const ro = new ResizeObserver(() => update());
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <DndContext
      collisionDetection={rectIntersection}
      onDragStart={drag.onDragStart}
      onDragEnd={drag.onDragEnd}
      onDragCancel={drag.onDragCancel}
      onDragOver={drag.onDragOver}
    >

      <DragOverlay>
        {drag.draggedItem && (
          <div
            className={`px-2 py-1 rounded-sm border text-sm opacity-90 ${
              drag.draggedItem.type === 'constant'
                ? 'border-rule border-l-4 border-l-ink bg-newsprint-dark text-ink italic'
                : 'border-rule bg-newsprint-dark'
            }`}
          >
            {drag.draggedItem.label}
          </div>
        )}
      </DragOverlay>

      <section className="flex gap-6 border border-rule rounded-sm p-6 bg-newsprint-panel w-full min-w-0">
        {/* overflow-x-auto: wide expression graphs must not paint over the palettes (sibling column). */}
        <div
          ref={editorMainColRef}
          className="flex min-h-0 min-w-0 flex-1 flex-col gap-4 overflow-x-auto overflow-y-visible"
        >
          <OperatorPalette setDraggedItem={drag.setDraggedItem} />
          <ExpressionCanvas
            expressionGraph={expressionGraph}
            layoutBudgetPx={layoutBudgetPx}
            rootRef={rootRef}
            isDragging={drag.isDragging}
            isPromoteTarget={drag.isPromoteTarget}
            onRequestEditSeries={dialog.handleRequestEditSeries}
            hoveredPath={drag.hoveredPath}
            instruments={instruments}
            indicators={indicators}
            onInsertLiteral={onInsertLiteral}
            onUpdateEpsilon={onUpdateEpsilon}
            onRemoveOperand={onRemoveOperand}
            onClearExpression={onClearExpression}
          />
        </div>
        <div className="relative z-10 flex w-64 shrink-0 flex-col gap-4 bg-newsprint-panel">
          <AvailableSeries series={availableSeries} setDraggedItem={drag.setDraggedItem} />
          <AvailableConstants
            constants={constants}
            setDraggedItem={drag.setDraggedItem}
            onAddConstant={addConstant}
            onUpdateConstant={updateConstant}
            onRemoveConstant={removeConstant}
          />
          <AvailableFunctions setDraggedItem={drag.setDraggedItem} />
        </div>
      </section>

      <SeriesConfigDialog
        isOpen={dialog.dialogOpen}
        seriesLabel={dialog.dialogSeriesLabel}
        isOHLC={dialog.dialogIsOHLC}
        functionId={dialog.dialogFunctionId}
        existingTypes={dialog.dialogExistingTypes}
        expectedType={dialog.dialogExpectedType}
        compact={dialog.compactDialog}
        availableFields={dialog.dialogAvailableFields}
        defaultField={dialog.dialogDefaultField ?? undefined}
        initialField={
          dialog.seriesEditor && !dialog.compactDialog
            ? (expressionGraph.expressions[dialog.seriesEditor.operandId] as any)?.type ===
                'ohlc_series' || (expressionGraph.expressions[dialog.seriesEditor.operandId] as any)?.type === 'indicator_series'
              ? (expressionGraph.expressions[dialog.seriesEditor.operandId] as any).field
              : undefined
            : undefined
        }
        initialOffset={
          dialog.seriesEditor
            ? (expressionGraph.expressions[dialog.seriesEditor.operandId] as any)?.offset ?? 0
            : 0
        }
        onSubmit={(config: { offset: number; field?: string }) => {
          if (dialog.compactDialog && dialog.seriesEditor) {
            dialog.handleCompactSeriesAwareSubmit(config);
          } else {
            dialog.handleDialogSubmit({
              field: config.field ?? 'close',
              offset: config.offset,
            });
          }
          dialog.handleDialogCancel();
        }}
        onCancel={dialog.handleDialogCancel}
      />
      <Toast message={toast} onDismiss={() => setToast(null)} />
    </DndContext>
  );
};

export default ExpressionEditor;